import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, statusBarClass } from "@/components/app/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, Clock, Wrench, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/app/PhotoUploader";

export default function MyTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState(null); // 'done' or 'issue'
  const [remarks, setRemarks] = useState("");
  const [photo, setPhoto] = useState(null);

  const load = async () => {
    const { data } = await api.get("/tasks/my");
    setTasks(data);
    if (user?.id) {
      try {
        const { data: tk } = await api.get(`/tickets?assigned_to=${user.id}`);
        // Tickets I accepted that still need work
        setMyTickets((tk || []).filter((t) => !["resolved", "closed"].includes(t.status)));
      } catch (e) { /* non-blocking */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const open = (t, m) => { setActive(t); setMode(m); setRemarks(""); setPhoto(null); };

  const submit = async () => {
    if (!active) return;
    if (active.photo_required && !photo) { toast.error("Photo is required"); return; }
    if (active.remarks_required && !remarks.trim()) { toast.error("Remarks are required"); return; }
    try {
      const status = mode === "done" ? "completed" : "issue_found";
      await api.put(`/tasks/${active.id}`, { status, remarks: remarks || null, photo_path: photo });
      if (mode === "issue") {
        await api.post(`/tickets`, {
          property_id: active.property_id,
          zone_id: active.zone_id,
          area_id: active.area_id,
          task_id: active.id,
          category: active.name,
          description: remarks || "Issue reported by staff",
          priority: active.priority || "medium",
          photo_paths: photo ? [photo] : [],
        });
      }
      toast.success(mode === "done" ? "Task completed" : "Issue raised");
      setActive(null); setMode(null);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const pending = tasks.filter((t) => t.status === "pending" || t.status === "overdue" || t.status === "in_progress");
  const done = tasks.filter((t) => t.status === "completed");
  const issues = tasks.filter((t) => t.status === "issue_found");

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="my-tasks-page">
      <div>
        <span className="label-overline">My Tasks Today</span>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">{pending.length} to go</h1>
        <p className="text-sm text-zinc-500 mt-1">{done.length} done · {issues.length} flagged</p>
      </div>

      <div className="flex flex-col gap-4">
        {pending.length === 0 && done.length === 0 && issues.length === 0 && myTickets.length === 0 && (
          <div className="text-center text-zinc-500 py-12">No tasks assigned.</div>
        )}
        {[...pending, ...issues, ...done].map((t) => (
          <div key={t.id} className={`bg-white border border-zinc-200 rounded-lg p-4 ${statusBarClass(t.status)}`} data-testid={`my-task-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                </div>
                <h4 className="font-display text-lg font-bold tracking-tight mt-2">{t.name}</h4>
                {t.description && <p className="text-sm text-zinc-600 mt-1">{t.description}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.due_at ? new Date(t.due_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}</span>
                </div>
              </div>
            </div>
            {t.photo_path && (
              <img src={`${API}/files?path=${encodeURIComponent(t.photo_path)}`} alt="proof" className="mt-3 w-full h-40 object-cover rounded-md border border-zinc-200" />
            )}
            {t.remarks && <div className="mt-3 text-sm bg-zinc-50 p-2 rounded">{t.remarks}</div>}
            {(t.status === "pending" || t.status === "overdue" || t.status === "in_progress") && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button onClick={() => open(t, "done")} className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`mark-done-${t.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Done
                </Button>
                <Button onClick={() => open(t, "issue")} variant="outline" className="h-12 border-red-200 text-red-700 hover:bg-red-50 font-bold" data-testid={`mark-issue-${t.id}`}>
                  <AlertTriangle className="w-4 h-4 mr-1.5" /> Issue
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {myTickets.length > 0 && (
        <div className="space-y-3" data-testid="my-tickets-section">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-zinc-500" />
            <h2 className="font-display text-xl font-bold tracking-tight">My tickets</h2>
            <span className="text-xs text-zinc-400">{myTickets.length} accepted</span>
          </div>
          {myTickets.map((t) => (
            <Link
              key={t.id}
              to="/tickets"
              className={`block bg-white rounded-lg p-4 hover:bg-zinc-50 transition-colors ${statusBarClass(t.status)} ${t.is_overdue ? "border-2 border-red-500" : "border border-zinc-200"}`}
              data-testid={`my-ticket-${t.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityBadge priority={t.priority} />
                    <StatusBadge status={t.status} />
                    {t.assigned_to_department && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5">{t.assigned_to_department}</span>
                    )}
                    {t.is_overdue && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white rounded px-1.5 py-0.5">Overdue</span>
                    )}
                  </div>
                  <h4 className="font-display text-lg font-bold tracking-tight mt-2">{t.category}{t.room_no ? <span className="text-zinc-500 font-normal"> · Room {t.room_no}</span> : ""}</h4>
                  {t.description && <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{t.description}</p>}
                  {t.expected_closing_at && (
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Due {new Date(t.expected_closing_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</div>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-300 shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setMode(null); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{mode === "done" ? "Mark Done" : "Report Issue"}: {active?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Photo {active?.photo_required ? "(required)" : "(optional)"}</Label>
              <div className="mt-2">
                <PhotoUploader value={photo} onChange={setPhoto} testid="task-photo" />
              </div>
            </div>
            <div>
              <Label>Remarks {(active?.remarks_required || mode === "issue") ? "(required)" : "(optional)"}</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} placeholder="Add notes..." data-testid="task-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} className={mode === "done" ? "bg-emerald-600" : "bg-red-600"} data-testid="submit-task-action">
              {mode === "done" ? "Mark Done" : "Raise Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
